import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AbstractWidgetComponent, AppClientConfiguration, AppClientConfigurationService, ContextChange, DialogService, ResponseStatus, TranslationService, UserProfileDialogService } from '@sdbn/common';
import { Subscription } from 'rxjs';

import {
  LegendEntry, LegendPosition, MapConfiguration, MapConfigurationDetails, SaveLegendEntry, SaveMapConfiguration, SaveStationAssignment, Station, StationAssignment
} from '../../yard-map-configuration-model';
import { YardMapConfigurationService } from '../../yard-map-configuration.service';

@Component({
  selector: 'app-tmc-yard-yard-map-configuration',
  templateUrl: './yard-map-configuration.component.html',
  styleUrls: ['./yard-map-configuration.component.scss']
})
export class YardMapConfigurationComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  @ViewChild('mapContainer') mapContainer: any;

  defaultLegendEntryColor = '#b3b3b3';

  extendedStationTile = false;

  mapSelectionSubscription: Subscription;

  newStationAssignmentId = -1;
  newMapConfigurationId = -1;
  newLegendEntryId = -1;

  stations = new Array<Station>();
  configurations = new Array<MapConfiguration>();
  configuration = new MapConfigurationDetails();

  mapNameForm: FormGroup;
  mapSelectionForm: FormGroup;
  legendEntryForm: FormGroup;

  constructor(
    private configurationService: AppClientConfigurationService,
    private mapAdministrationService: YardMapConfigurationService,
    private dialog: MatDialog,
    public translationService: TranslationService,
    public userProfileDialogService: UserProfileDialogService,
    private dialogService: DialogService,
    private appClientConfiguration: AppClientConfiguration
  ) {
    super();
    this.setupFormFields();
    this.loadStations();
    this.loadMapConfigurations();
  }

  ngOnInit(): void {
    this.extendedStationTile = this.configurationService.isPropertyTrue('yardview.stations.numberplate.enabled', false);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.mapSelectionSubscription != null) {
      this.mapSelectionSubscription.unsubscribe();
    }
  }

  public onDragStationStart(event: DragEvent, station: Station) {
    event.dataTransfer.effectAllowed = 'copy';
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData('text/plain', '');

    this.configuration.draggedStation = station;
    this.configuration.draggedStation.grabOffsetX = event.offsetX;
    this.configuration.draggedStation.grabOffsetY = event.offsetY;

  }

  public onDragStationAssignmentStart(event: DragEvent, assignment: StationAssignment) {
    event.dataTransfer.effectAllowed = 'copy';
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData('text/plain', '');

    // Temporäre Selection wärend d+d um details komponente zu aktualisieren
    for (const s of this.configuration.stationAssignments) {
      s.selected = false;
    }
    assignment.selected = true;
    this.configuration.selectedStationAssignment = new StationAssignment({ x: assignment.x, y: assignment.y });

    const target = event.target as HTMLElement;
    const rect: DOMRect = target.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    this.configuration.draggedStationAssignment = assignment;
    this.configuration.draggedStationAssignment.grabOffsetX = offsetX;
    this.configuration.draggedStationAssignment.grabOffsetY = offsetY;
  }

  public onDragStationEnd(event: DragEvent) {
    this.configuration.draggedStation = null;
  }

  public onDragOverMap(event: DragEvent) {
    if (this.configuration.draggedStation != null || this.configuration.draggedStationAssignment != null) {
      event.preventDefault();
      return;
    }
  }

  public onDropToMap(event: DragEvent) {
    event.preventDefault();

    // Position des Map Containers innerhalb der Seite ermitteln
    const rect: DOMRect = this.mapContainer.nativeElement.getBoundingClientRect();
    // console.log(rect);

    // Drag position relativ zu MapContainer berechnen
    const dragPosX = event.clientX - rect.x;
    const dragPosY = event.clientY - rect.y;

    if (this.configuration.draggedStation != null) {

      const assignment = new StationAssignment({
        id: this.newStationAssignmentId--,
        name: this.configuration.draggedStation.name,
        identifier: this.configuration.draggedStation.identifier,
        isWaitingArea: this.configuration.draggedStation.isWaitingArea
      });
      // grab offset subtrahieren
      assignment.x = dragPosX - this.configuration.draggedStation.grabOffsetX;
      assignment.y = dragPosY - this.configuration.draggedStation.grabOffsetY;
      assignment.stationId = this.configuration.draggedStation.id;
      this.configuration.stationAssignments.push(assignment);

      event.preventDefault();
      this.selectStationAssignment(assignment);
      this.configuration.draggedStation = null;
      this.configuration.dirty = true;
    }

    if (this.configuration.draggedStationAssignment != null) {
      const stationAssignment = this.configuration.stationAssignments.find(s => s.id === this.configuration.draggedStationAssignment.id);
      // grab offset subtrahieren
      stationAssignment.x = dragPosX - this.configuration.draggedStationAssignment.grabOffsetX;
      stationAssignment.y = dragPosY - this.configuration.draggedStationAssignment.grabOffsetY;
      event.preventDefault();
      this.selectStationAssignment(stationAssignment);
      this.configuration.draggedStationAssignment = null;
      this.configuration.dirty = true;
    }
  }

  public onDragLegendEntryStart(event: DragEvent, entry: LegendEntry) {
    event.dataTransfer.effectAllowed = 'copy';
    // IOS geht nur wenn gesetzt
    event.dataTransfer.setData('text/plain', '');

    this.configuration.draggedLegendEntry = entry;
  }

  public onDragOverStation(event: DragEvent) {
    if (this.configuration.draggedLegendEntry != null) {
      event.preventDefault();
    }
  }

  public dropLegendListEntry(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.configuration.legendEntries, event.previousIndex, event.currentIndex);
    this.configuration.dirty = true;
  }

  public onDropToStation(event: DragEvent, station: StationAssignment) {
    if (this.configuration.draggedLegendEntry != null) {

      station.color = this.configuration.draggedLegendEntry.color;
      station.legendEntryId = this.configuration.draggedLegendEntry.id;

      this.configuration.dirty = true;

      event.preventDefault();
    }
  }

  public selectStationAssignment(assignment: StationAssignment) {
    for (const s of this.configuration.stationAssignments) {
      s.selected = false;
    }
    assignment.selected = true;
    this.configuration.selectedStationAssignment = assignment;
  }

  public colorPickerChanged() {
    this.legendEntryForm.markAsDirty();
    for (const station of this.configuration.stationAssignments) {
      if (station.legendEntryId === this.configuration.editableLegendEntry.id) {
        station.color = this.configuration.editableLegendEntry.color;
        if (this.configuration.editableLegendEntry.fontColor) {
          station.fontColor = this.configuration.editableLegendEntry.fontColor;
        } else {
          station.color = 'black';
        }
      }
    }
    this.configuration.dirty = true;
  }

  public editLegendEntry(entry: LegendEntry) {
    this.configuration.editableLegendEntry = entry;
    this.legendEntryForm.reset({ name: entry.name, color: entry.color, fontColor: entry.fontColor });
  }

  public removeLegendentry(entry: LegendEntry) {

    if (entry.id <= 0) {
      const index = this.configuration.legendEntries.indexOf(entry);
      this.configuration.legendEntries.splice(index, 1);
    } else {
      entry.deleted = true;
    }

    for (const assignment of this.configuration.stationAssignments) {
      if (assignment.legendEntryId === entry.id) {
        assignment.legendEntryId = null;
        assignment.color = null;
      }
    }
    this.configuration.dirty = true;
  }

  public addLegendEntry() {

    const entry = new LegendEntry(
      {
        name: null,
        color: this.defaultLegendEntryColor, id: this.newLegendEntryId--
      }
    );
    this.configuration.legendEntries.push(entry);
    this.configuration.dirty = true;
    this.editLegendEntry(entry);
  }

  public onStationAssignmentRemoved(assignment: StationAssignment) {
    const deletedAssignment = this.configuration.stationAssignments.find(a => a.id === assignment.id);
    deletedAssignment.deleted = true;
    if (assignment.id <= 0) {
      const index = this.configuration.stationAssignments.indexOf(deletedAssignment);
      this.configuration.stationAssignments.splice(index, 1);
    }
    this.configuration.dirty = true;
  }

  public addMapConfiguration() {

    if (this.configuration.dirty) {

      this.dialogService.showConfirm(
        'tmc-yard.ui.admin.yard-map-configuration.discard.confirm-dialog.title',
        'tmc-yard.ui.admin.yard-map-configuration.discard.confirm-dialog.message'
      ).afterClosed().subscribe(() => {
        this.handleAddMapConfiguration();
      });

    } else {
      this.handleAddMapConfiguration();
    }
  }

  private handleAddMapConfiguration() {
    this.unsubscribeFromMapSubscription();
    try {
      const configuration = new MapConfigurationDetails();
      configuration.id = this.newMapConfigurationId--;
      configuration.width = 800;
      configuration.height = 600;
      this.setConfiguration(configuration);
      this.mapSelectionForm.reset({ selectedMap: null });
    } finally {
      this.subscribeToMapSelection();
    }
  }

  public saveMapConfiguration() {

    const request = new SaveMapConfiguration();
    request.id = this.configuration.id;
    request.name = this.configuration.name;
    request.width = this.configuration.width;
    request.height = this.configuration.height;
    request.legendPosition = this.configuration.legendPosition;
    request.mapResourceUUID = this.configuration.mapResourceUUID;

    for (const assignment of this.configuration.stationAssignments) {
      const saveAssignment = new SaveStationAssignment();
      saveAssignment.deleted = assignment.deleted;
      saveAssignment.id = assignment.id;
      saveAssignment.legendEntryId = assignment.legendEntryId;
      saveAssignment.stationId = assignment.stationId;
      saveAssignment.x = assignment.x;
      saveAssignment.y = assignment.y;
      request.stationAssignments.push(saveAssignment);
    }

    let sortIndex = 0;
    for (const entry of this.configuration.legendEntries) {
      const saveLegendEntry = new SaveLegendEntry();
      saveLegendEntry.deleted = entry.deleted;
      saveLegendEntry.id = entry.id;
      saveLegendEntry.name = entry.name;
      saveLegendEntry.color = entry.color;
      saveLegendEntry.fontColor = entry.fontColor || '#000000';
      saveLegendEntry.sortIndex = sortIndex++;
      request.legendEntries.push(saveLegendEntry);
    }

    this.mapAdministrationService.saveMapConfiguration(request).subscribe(
      response => {

        this.unsubscribeFromMapSubscription();
        try {
          if (this.configuration.id <= 0) {
            this.configuration.id = response.mapConfigurationId;
            const newConfiguration = new MapConfiguration({ id: this.configuration.id, name: this.configuration.name });
            this.configurations.push(newConfiguration);
            this.mapSelectionForm.reset({ selectedMap: this.configuration.id });
          } else {
            const conf = this.configurations.find(c => c.id === this.configuration.id);
            conf.name = this.configuration.name;
          }
          this.loadMapConfiguration(this.configuration.id);
        } finally {
          this.subscribeToMapSelection();
        }
      }
    );

  }

  public deleteMapConfiguration() {

    this.dialogService.showConfirm(
      'tmc-yard.ui.admin.yard-map-configuration.delete.confirm-dialog.title',
      'tmc-yard.ui.admin.yard-map-configuration.delete.confirm-dialog.message'
    ).afterClosed().subscribe(() => {
      this.handleDeleteMapConfiguration();
    });

  }

  private handleDeleteMapConfiguration() {
    if (this.configuration.id <= 0) {
      this.removeMapFromConfigurations(this.configuration);
    } else {
      this.mapAdministrationService.deleteMapConfiguration(this.configuration.id).subscribe(
        response => {
          if (ResponseStatus.SUCCESS === response.status) {
            this.removeMapFromConfigurations(this.configuration);
          }
        }
      );
    }
  }

  private removeMapFromConfigurations(configuration: MapConfiguration) {
    const deletedConfiguration = this.configurations.find(c => c.id === configuration.id);
    const index = this.configurations.indexOf(deletedConfiguration);
    this.configurations.splice(index, 1);

    if (this.configurations.length === 0) {
      this.handleAddMapConfiguration();
    } else {
      let newSelection = this.configurations[index - 1];
      if (newSelection == null) {
        newSelection = this.configurations[0];
      }
      this.unsubscribeFromMapSubscription();
      try {
        this.mapSelectionForm.reset({ selectedMap: newSelection.id });
        this.loadMapConfiguration((newSelection.id));
      } finally {
        this.subscribeToMapSelection();
      }
    }
  }

  private setupFormFields() {
    this.mapSelectionForm = new FormGroup({
      selectedMap: new FormControl(null, [Validators.required]),
    });

    this.mapNameForm = new FormGroup({
      name: new FormControl(null, [Validators.required]),
    });


    this.mapNameForm.controls.name.valueChanges.subscribe(
      value => {
        if (this.mapNameForm.controls.name.dirty) {
          this.configuration.name = value;
          this.configuration.dirty = true;
        }
      }
    );

    this.subscribeToMapSelection();

    this.legendEntryForm = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      color: new FormControl(null, [Validators.required]),
      fontColor: new FormControl(null, [Validators.required]),
    });

    this.legendEntryForm.valueChanges.subscribe(
      value => {
        if (this.legendEntryForm.dirty && this.configuration.editableLegendEntry != null) {
          this.configuration.editableLegendEntry.name = this.legendEntryForm.controls.name.value;
          this.configuration.editableLegendEntry.color = this.legendEntryForm.controls.color.value;
          this.configuration.editableLegendEntry.fontColor = this.legendEntryForm.controls.fontColor.value;
          this.configuration.dirty = true;
        }
      }
    );
  }

  private subscribeToMapSelection() {
    this.unsubscribeFromMapSubscription();

    this.mapSelectionSubscription = this.mapSelectionForm.controls.selectedMap.valueChanges.subscribe(
      value => {
        if (this.mapSelectionForm.controls.selectedMap.dirty && value > 0) {
          if (this.configuration.dirty) {

            this.dialogService.showConfirm(
              'tmc-yard.ui.admin.yard-map-configuration.discard.confirm-dialog.title',
              'tmc-yard.ui.admin.yard-map-configuration.discard.confirm-dialog.message'
            ).afterClosed().subscribe(() => {
              this.loadMapConfiguration(value);
            });

          } else {
            this.loadMapConfiguration(value);
          }
        }
      }
    );
  }

  private unsubscribeFromMapSubscription() {
    if (this.mapSelectionSubscription) {
      this.mapSelectionSubscription.unsubscribe();
      this.mapSelectionSubscription = null;
    }
  }

  private loadStations() {
    this.stations = [];
    this.mapAdministrationService.getStations().subscribe(
      stations => {
        for (const s of stations) {
          this.stations.push(new Station(s));
        }
      }
    );
  }

  private loadMapConfigurations() {

    this.mapAdministrationService.loadMapConfigurations().subscribe(
      configurations => {
        this.configurations = [];
        for (const conf of configurations) {
          this.configurations.push(new MapConfiguration(conf));
        }
        if (this.configurations.length > 0) {
          this.unsubscribeFromMapSubscription();
          try {
            this.mapSelectionForm.reset({ selectedMap: this.configurations[0].id });
            this.loadMapConfiguration(this.configurations[0].id);
          } finally {
            this.subscribeToMapSelection();
          }
        } else {
          this.handleAddMapConfiguration();
        }
      }
    );
  }

  private loadMapConfiguration(configurationId: number) {

    this.mapAdministrationService.loadMapConfiguration(configurationId).subscribe(
      details => {

        const configuration = new MapConfigurationDetails();
        configuration.id = details.id;
        configuration.name = details.name;
        configuration.height = details.height;
        configuration.width = details.width;

        if (details.legendPosition != null) {
          for (const key of Object.keys(LegendPosition)) {
            if (key === details.legendPosition) {
              configuration.legendPosition = LegendPosition[key];
            }
          }
        }

        configuration.mapResourceUUID = details.mapResourceUUID;
        configuration.mapURL = this.appClientConfiguration.serverURLRoot + '/client/resource/archive/' + details.mapResourceUUID;

        for (const entry of details.legendEntries) {
          configuration.legendEntries.push(new LegendEntry(entry));
        }

        for (const assignment of details.stationAssignments) {
          configuration.stationAssignments.push(new StationAssignment(assignment));
        }
        this.setConfiguration(configuration);
      }
    );

  }

  private setConfiguration(configuration: MapConfigurationDetails) {
    this.configuration = configuration;
    this.mapNameForm.reset({ name: this.configuration.name });
  }

  onContextChange(contextChange: ContextChange) {
  }
  onSetElementAndContext() {
  }

}
